import { Controller } from "@hotwired/stimulus";
import { BottomSheetSwipe } from "../helpers/bottom_sheet_swipe";

// The one parameter that opens THIS dialog, and therefore the only one closing
// it may remove. Not post_dialog_src: a post dialog can still be open
// underneath, and taking its state away would leave it unrestorable. Everything
// else in the query string belongs to the page below and has to survive.
const DIALOG_URL_PARAM = "dialog_src";

export default class extends Controller {
  static targets = ["modal"];

  static values = {
    closedBy: String,
  };

  mousedownOutside = false;
  isLoadingState = false;
  isClosing = false; // Prevent race conditions in close()

  connect() {
    this.modalTarget.addEventListener(
      "close",
      this.enableBodyScroll.bind(this),
    );

    this.submitButton = this.modalTarget.querySelector("[type='submit']");

    this.modalTarget.addEventListener("cancel", (event) => {
      if (this.modalTarget.attributes.closedby?.value === "none") {
        event.preventDefault();
        event.stopPropagation();

        requestAnimationFrame(() => {
          this.modalTarget.showModal();
        });
      }
    });

    if (this.modalTarget.dataset.dialogSrc) {
      this.show();
    }

    // Initialize swipe-to-dismiss for mobile bottom sheets
    this.swipeHandler = new BottomSheetSwipe(this.modalTarget, {
      onDismiss: () => this.close({ confirmation: false }),
      canDismiss: () => this.canSwipeDismiss(),
    });
  }

  disconnect() {
    this.swipeHandler?.destroy();
    this.modalTarget.close();
    this.modalTarget.removeEventListener(
      "close",
      this.enableBodyScroll.bind(this),
    );
  }

  show() {
    // Safari iOS 26 fix: Set both html and body background
    document.documentElement.style.backgroundColor = "#ffffff";
    document.body.style.backgroundColor = "#ffffff";
    requestAnimationFrame(() => {
      this.modalTarget.showModal();
      this.disableBodyScroll();
    });
  }

  close(eventOrOptions = {}) {
    // Method is called both via Stimulus actions (with an Event) and
    // programmatically (with an options object) — normalize both.
    let event = null;
    let options = {};
    if (eventOrOptions instanceof Event) {
      event = eventOrOptions;
    } else {
      options = eventOrOptions;
    }

    // Prevent race conditions from concurrent close() calls (e.g., swipe + form submit)
    if (this.isClosing) return;
    this.isClosing = true;

    // Prevent closing during loading state unless explicitly forced (e.g., after successful submit)
    if (this.isLoadingState && options.forceClose !== true) {
      this.isClosing = false;
      return;
    }

    const dropzoneFields = document.querySelectorAll(".dz-clickable");

    let hasUploadedFiles = false;
    dropzoneFields.forEach((field) => {
      if (field.querySelector(".dz-processing")) {
        hasUploadedFiles = true;
      }
    });

    let closeDialog = true;
    let confirmation = hasUploadedFiles && !this.isLoadingState;

    if (options.confirmation !== undefined) {
      confirmation = options.confirmation;
    }

    if (confirmation) {
      const confirmed = confirm(this.modalTarget.dataset.closeConfirm);

      if (!confirmed) {
        closeDialog = false;
      }
    }

    if (closeDialog) {
      // Skip URL manipulation and clearing for info dialogs (data-dialog-skip-clear attribute)
      // This prevents info dialogs from interfering with parent dialog state
      if (!this.modalTarget.hasAttribute("data-dialog-skip-clear")) {
        this.clearDialog();
      } else if (event) {
        event.preventDefault(); // Prevent form submission for info/skipped dialogs
      }
      this.modalTarget.close();
    }

    this.isClosing = false;
  }

  // Only the dialog's own trace is removed from the URL. Dropping the whole
  // query string took unrelated state with it: closing a share sheet on a
  // program detail opened with ?range=7d left the page showing seven-day figures
  // under a URL that no longer said so, and a reload would then contradict the
  // screen.
  clearDialog() {
    const url = new URL(window.location.href);
    url.searchParams.delete(DIALOG_URL_PARAM);
    window.history.replaceState({}, document.title, `${url.pathname}${url.search}${url.hash}`);

    document.querySelector("#dialog_modal").innerHTML = "";
  }

  disableBodyScroll() {
    document.body.style.overflow = "hidden";
  }

  enableBodyScroll() {
    // Only enable scroll if NO dialogs are still open
    // This prevents scroll unlock when closing nested dialogs (e.g., info dialog over parent dialog)
    const openDialogs = document.querySelectorAll("dialog[open]");
    if (openDialogs.length === 0) {
      document.body.style.removeProperty("overflow");
      // Safari iOS 26 fix
      document.documentElement.style.removeProperty("background-color");
      document.body.style.removeProperty("background-color");
    }
  }

  submitEnd(event) {
    if (event.detail.success) {
      // Force close on success, bypassing loading state check to prevent race conditions
      this.close({ confirmation: false, forceClose: true });
    }
  }

  setLoadingState(isLoading) {
    this.isLoadingState = isLoading;
    this.modalTarget.toggleAttribute("data-loading", isLoading);
  }

  /**
   * Check if swipe-to-dismiss should be allowed
   */
  canSwipeDismiss() {
    // Block during loading state
    if (this.isLoadingState) return false;

    // Block if closedby="none"
    if (this.modalTarget.getAttribute("closedby") === "none") return false;

    // Block if uploads are in progress
    const uploading = this.modalTarget.querySelector(".dz-processing");
    if (uploading) return false;

    return true;
  }
}
